import { defineStore } from "pinia";
import { ref } from "vue";
import useMyFetch from "@stores/useMyFetch";
import handleError from "@utils/composables/handleError";
import { getClientId } from "@utils/clientId";
import { captureEvent } from "@/lib/analytics";
import type { PricerQuotePreview } from "@/types";

export const usePricerQuoteStore = defineStore("pricerQuote", () => {
  const preview = ref<PricerQuotePreview | null>(null);
  const error = ref<string | null>(null);
  const isLoading = ref(false);
  const isGenerating = ref(false);

  const fetchPreview = async (projectToken: string) => {
    isLoading.value = true;
    preview.value = null;
    error.value = null;
    try {
      const {
        response,
        data,
        error: fetchError,
      } = await useMyFetch(`/pricer/projects/${projectToken}/hubspot/quote`).json<{ quote: PricerQuotePreview }>();
      if (response.value?.ok && data.value?.quote) preview.value = data.value.quote;
      else error.value = String(fetchError.value || "HubSpot could not be reached");
    } catch (error) {
      handleError(error);
    } finally {
      isLoading.value = false;
    }
  };

  const generateQuote = async (projectToken: string) => {
    isGenerating.value = true;
    error.value = null;
    try {
      const { response, error: fetchError } = await useMyFetch(
        `/pricer/projects/${projectToken}/hubspot/quote?client_id=${getClientId()}`,
      )
        .post()
        .json<{ progress_id: string }>();
      const ok = Boolean(response.value?.ok);
      if (ok) captureEvent("pricer_quote_generated", { project_token: projectToken });
      else error.value = String(fetchError.value || "The quote could not be generated");
      return ok;
    } catch (error) {
      handleError(error);
      return false;
    } finally {
      isGenerating.value = false;
    }
  };

  return { preview, error, isLoading, isGenerating, fetchPreview, generateQuote };
});
